import { CompaniesListingAction } from "@/components/company/companies-listing-action";
import { useApi } from "@/hooks/useApi";
import type { CompanyMentionCollection } from "@/lib/company-mention-nodes";
import {
  resolveTableRows,
  tableRowKey,
  type RowToSave,
} from "@/lib/company-table-rows";
import type {
  CompanySkipReason,
  ResolvedCompanies,
} from "@/lib/resolved-companies";
import { useCallback, useMemo, useRef, type RefObject } from "react";

type CompanyIdListener = (companyId: string) => void;

/** Why a row may join the list on a later save: a failure, or a spent day. */
const RETRYABLE_REASONS: ReadonlySet<CompanySkipReason> = new Set([
  "unavailable",
  "rate_limited",
]);

type CachedResolution = {
  readonly rowsKey: string;
  readonly resolution: Promise<ResolvedCompanies>;
  /** Ids placed so far, replayed to a caller that joins once some are in. */
  readonly found: string[];
  readonly listeners: Set<CompanyIdListener>;
};

// The rendered table, fed to the AI so a list created from it comes pre-titled.
// Long tables are cut off: the first rows already carry the common thread.
const MAX_TABLE_CONTEXT_LENGTH = 2000;

/**
 * The table as the user reads it — the listing-metadata prompt takes it from
 * here. Read cell by cell from the text nodes rather than through `innerText`,
 * which lays the table out and so takes in CSS-generated text too: a citation
 * pill's site name, written by `::before`, would land in the cells.
 */
function tableContext(table: HTMLElement | null): string | undefined {
  const text = [...(table?.querySelectorAll("tr") ?? [])]
    .map((row) =>
      [...row.querySelectorAll("th, td")]
        .map((cell) => cell.textContent?.trim() ?? "")
        .join("\t")
    )
    .join("\n")
    .trim();
  return text ? text.slice(0, MAX_TABLE_CONTEXT_LENGTH) : undefined;
}

/**
 * Resolves the rows to company ids once, shared by every caller: resolving or
 * saving a row enriches an unknown company, so it only ever starts once the
 * user has committed to an action — a list picked, or a new one created —
 * never on merely opening the picker or the creation dialog. A caller's
 * `onResolved` hears every id, those found before it joined included.
 */
function useSharedRowsResolution(
  rows: readonly RowToSave[],
): (onResolved?: CompanyIdListener) => Promise<ResolvedCompanies> {
  const api = useApi();

  // Identity of the cited companies, so a table whose rows changed — a
  // re-streamed turn, an edited question — never reuses the previous lookup.
  const rowsKey = useMemo(() => rows.map(tableRowKey).join("|"), [rows]);
  const resolutionRef = useRef<CachedResolution | null>(null);

  const startResolution = useCallback((): CachedResolution => {
    const found: string[] = [];
    const listeners = new Set<CompanyIdListener>();
    const resolution = resolveTableRows(api, rows, (companyId) => {
      found.push(companyId);
      listeners.forEach((listener) => listener(companyId));
    }).then((resolved) => {
      if (
        resolved.companiesIds.length === 0 ||
        resolved.skipped.some(({ reason }) => RETRYABLE_REASONS.has(reason))
      ) {
        // Nothing placed, or rows the toast tells to try again later or
        // tomorrow: drop the shared promise so the next save asks for them
        // again. The rows already saved are found again at no cost.
        resolutionRef.current = null;
      }
      return resolved;
    });
    const started = { rowsKey, resolution, found, listeners };
    resolutionRef.current = started;
    return started;
  }, [api, rows, rowsKey]);

  return useCallback(
    (onResolved?: CompanyIdListener): Promise<ResolvedCompanies> => {
      const cached = resolutionRef.current;
      const run = cached?.rowsKey === rowsKey ? cached : startResolution();
      if (onResolved) {
        run.found.forEach(onResolved);
        run.listeners.add(onResolved);
        const unsubscribe = (): void => {
          run.listeners.delete(onResolved);
        };
        run.resolution.then(unsubscribe, unsubscribe);
      }
      return run.resolution;
    },
    [rowsKey, startResolution],
  );
}

type MarkdownTableListActionsProps = {
  /** The companies the table cites: those an identifier resolves, and the
   *  others (see `collectCompanyMentions`), saved from their tag. */
  readonly companies: CompanyMentionCollection;
  readonly tableRef: RefObject<HTMLDivElement | null>;
};

/**
 * "Save to a list" action for the companies a markdown table cites, so a chat
 * result can be turned into a listing without re-finding each company by hand —
 * every row of it, those the chat named without any identifier included.
 * Renders nothing when the table cites no company.
 */
export function MarkdownTableListActions({
  companies,
  tableRef,
}: MarkdownTableListActionsProps) {
  const { mentions, unidentified } = companies;
  if (mentions.length + unidentified.length === 0) return null;
  return (
    <TableRowsListAction
      mentions={mentions}
      unidentified={unidentified}
      tableRef={tableRef}
    />
  );
}

type TableRowsListActionProps = CompanyMentionCollection & {
  readonly tableRef: RefObject<HTMLDivElement | null>;
};

/** The picker over a non-empty table — mounted only then, so an empty or
 *  still-streaming table holds no resolution state. */
function TableRowsListAction({
  mentions,
  unidentified,
  tableRef,
}: TableRowsListActionProps) {
  const rows: RowToSave[] = useMemo(
    () => [
      ...mentions.map((params): RowToSave => ({ kind: "identified", params })),
      ...unidentified.map((tag): RowToSave => ({ kind: "unidentified", tag })),
    ],
    [mentions, unidentified],
  );
  const resolve = useSharedRowsResolution(rows);

  return (
    <CompaniesListingAction
      resolveCompaniesIds={resolve}
      // Lets the creation dialog pre-fill its title and description from the table.
      searchQuery={() => tableContext(tableRef.current)}
    />
  );
}
